import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Modal } from "@/components/ui/modal";
import { useContactStore } from "@/store/contactStore";
import { useContactAttributeDefinitionStore } from "@/store/contactAttributeDefinitionStore";
import { useAuthStore } from "@/store/authStore";
import type { AttributeDataType, ContactChannel, ContactConsent, ConsentStatus, ContactStatus, IdentifierType, UpdateContactPayload } from "@/types/contacts";

const STATUS_COLORS: Record<ContactStatus, string> = {
  Active: "bg-green-500/10 text-green-600",
  Unsubscribed: "bg-yellow-500/10 text-yellow-600",
  Bounced: "bg-red-500/10 text-red-600",
  Deleted: "bg-muted text-muted-foreground",
  Pending: "bg-blue-500/10 text-blue-500",
};

const IDENTIFIER_LABELS: Record<IdentifierType, string> = {
  Email: "Email",
  Phone: "Phone",
  ExternalId: "External ID",
  MemberNumber: "Member #",
  CustomKey: "Custom key",
};

type Tab = "profile" | "identifiers" | "attributes" | "consent" | "history";

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  ContactCreated: "Contact created",
  ProfileUpdated: "Profile updated",
  AttributeSet: "Attribute set",
  AttributeRemoved: "Attribute removed",
  IdentifierAdded: "Identifier added",
  IdentifierRemoved: "Identifier removed",
  ConsentRecorded: "Consent recorded",
  EmailOpened: "Email opened",
  EmailClicked: "Link clicked",
  EmailBounced: "Email bounced",
  EmailUnsubscribed: "Unsubscribed",
  EmailComplained: "Marked as spam",
  ContactAnonymized: "Personal data erased",
};

function formatActivityType(type: string): string {
  return ACTIVITY_TYPE_LABELS[type] ?? type;
}

interface AddIdentifierModalProps {
  accountId: string;
  contactId: string;
  onClose: () => void;
}

function AddIdentifierModal({ accountId, contactId, onClose }: AddIdentifierModalProps) {
  const { addIdentifier, isSaving, error } = useContactStore();
  const [type, setType] = useState<IdentifierType>("Email");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    try {
      await addIdentifier(accountId, contactId, { identifierType: type, value: value.trim(), isPrimary, label: label || undefined });
      onClose();
    } catch {
      // error shown from store
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as IdentifierType)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {(["Email", "Phone", "ExternalId", "MemberNumber", "CustomKey"] as IdentifierType[]).map((t) => (
            <option key={t} value={t}>{IDENTIFIER_LABELS[t]}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Value</label>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Label <span className="text-muted-foreground">(optional)</span></label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. work, personal"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="rounded border-border" />
        Set as primary
      </label>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">Cancel</button>
        <Button type="submit" disabled={isSaving || !value.trim()}>{isSaving ? "Adding…" : "Add identifier"}</Button>
      </div>
    </form>
  );
}

export default function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";

  const { currentContact, isLoading, isSaving, error, fetchContact, updateContact, deleteContact, removeIdentifier, setAttributes, fetchConsents, consents, isLoadingConsents, consentsContactId, fetchActivityLog, activityLog, isLoadingActivityLog, activityLogContactId } = useContactStore();
  const { definitions, fetchDefinitions } = useContactAttributeDefinitionStore();
  const [tab, setTab] = useState<Tab>("profile");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddIdentifier, setShowAddIdentifier] = useState(false);

  // Profile edit state
  const [editing, setEditing] = useState(false);
  const [profileDraft, setProfileDraft] = useState<UpdateContactPayload>({});

  // Attributes edit state
  const [attrDraft, setAttrDraft] = useState<Record<string, string>>({});
  const [editingAttrs, setEditingAttrs] = useState(false);
  const [newAttrKey, setNewAttrKey] = useState("");
  const [newAttrValue, setNewAttrValue] = useState("");

  useEffect(() => {
    if (accountId && contactId) {
      fetchContact(accountId, contactId);
      fetchDefinitions(accountId);
    }
  }, [accountId, contactId, fetchContact, fetchDefinitions]);

  useEffect(() => {
    if (tab === "consent" && accountId && contactId && consentsContactId !== contactId && !isLoadingConsents) {
      fetchConsents(accountId, contactId);
    }
  }, [tab, accountId, contactId, consentsContactId, isLoadingConsents, fetchConsents]);

  useEffect(() => {
    if (tab === "history" && accountId && contactId && activityLogContactId !== contactId && !isLoadingActivityLog) {
      fetchActivityLog(accountId, contactId);
    }
  }, [tab, accountId, contactId, activityLogContactId, isLoadingActivityLog, fetchActivityLog]);

  // Reset the drafts whenever a fresh copy of the contact arrives (adjusting state during render,
  // instead of an effect that would render the stale drafts first)
  const [draftSource, setDraftSource] = useState<typeof currentContact>(null);
  if (currentContact !== draftSource) {
    setDraftSource(currentContact);
    if (currentContact) {
      setProfileDraft({
        firstName: currentContact.firstName,
        lastName: currentContact.lastName,
        company: currentContact.company,
        jobTitle: currentContact.jobTitle,
        languageCode: currentContact.languageCode,
        countryCode: currentContact.countryCode,
        timezone: currentContact.timezone,
        notes: currentContact.notes,
        tags: currentContact.tags,
      });
      setAttrDraft({ ...currentContact.attributeDictionary });
    }
  }

  const handleSaveProfile = async () => {
    if (!accountId || !contactId) return;
    await updateContact(accountId, contactId, profileDraft);
    setEditing(false);
  };

  const handleToggleMarketingOptin = async () => {
    if (!accountId || !contactId || !currentContact) return;
    try {
      await updateContact(accountId, contactId, { marketingEmailOptin: !currentContact.marketingEmailOptin });
      // The change also writes a consent record and an activity entry; refresh them if shown
      if (consentsContactId === contactId) fetchConsents(accountId, contactId);
      if (activityLogContactId === contactId) fetchActivityLog(accountId, contactId);
    } catch {
      // error shown from store
    }
  };

  const handleDelete = async () => {
    if (!accountId || !contactId) return;
    await deleteContact(accountId, contactId);
    navigate("/audience/contacts");
  };

  const handleRemoveIdentifier = async (type: IdentifierType, value: string) => {
    if (!accountId || !contactId) return;
    await removeIdentifier(accountId, contactId, type, value);
  };

  const handleSaveAttributes = async () => {
    if (!accountId || !contactId) return;
    await setAttributes(accountId, contactId, attrDraft);
    setEditingAttrs(false);
  };

  const handleAddAttr = () => {
    if (!newAttrKey.trim()) return;
    setAttrDraft((prev) => ({ ...prev, [newAttrKey.trim()]: newAttrValue }));
    setNewAttrKey("");
    setNewAttrValue("");
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center py-24">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading…
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (!isLoading && !currentContact) {
    return (
      <ProtectedRoute>
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">{error ?? "Contact not found."}</p>
          <button onClick={() => navigate("/audience/contacts")} className="mt-4 text-sm text-primary hover:underline">← Back to contacts</button>
        </div>
      </ProtectedRoute>
    );
  }

  const contact = currentContact!;
  const displayName = contact.fullName?.trim() || contact.primaryEmail || contact.id.slice(0, 8);

  return (
    <ProtectedRoute>
      <div className="lw-contact-detail container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back */}
          <button onClick={() => navigate("/audience/contacts")} className="mb-6 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Contacts
          </button>

          {/* Header */}
          <div className="lw-contact-header flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-foreground">{displayName}</h1>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[contact.status]}`}>
                  {contact.status}
                </span>
              </div>
              {contact.primaryEmail && <p className="text-sm text-muted-foreground">{contact.primaryEmail}</p>}
              {contact.company && <p className="text-sm text-muted-foreground">{contact.company}{contact.jobTitle ? ` · ${contact.jobTitle}` : ""}</p>}
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${contact.marketingEmailOptin ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}`}>
                  {contact.marketingEmailOptin ? "Opted in to marketing emails" : "Not opted in to marketing emails"}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMarketingOptin}
                  disabled={isSaving}
                  className="text-xs text-primary hover:underline disabled:opacity-50"
                >
                  {contact.marketingEmailOptin ? "Opt out" : "Record opt-in"}
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="rounded-md border border-destructive/30 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
            >
              Delete
            </button>
          </div>

          {/* Tabs */}
          <div className="lw-contact-tabs flex gap-1 border-b border-border mb-6">
            {(["profile", "identifiers", "attributes", "consent", "history"] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                  tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Profile tab */}
          {tab === "profile" && (
            <div className="lw-contact-profile space-y-6">
              <div className="flex justify-end">
                {editing ? (
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(false)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-accent transition-colors">Cancel</button>
                    <Button onClick={handleSaveProfile} disabled={isSaving}>{isSaving ? "Saving…" : "Save"}</Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setEditing(true)}>Edit</Button>
                )}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {([
                  ["First name", "firstName"],
                  ["Last name", "lastName"],
                  ["Company", "company"],
                  ["Job title", "jobTitle"],
                  ["Language code", "languageCode"],
                  ["Country code", "countryCode"],
                  ["Timezone", "timezone"],
                ] as [string, keyof UpdateContactPayload][]).map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
                    {editing ? (
                      <input
                        value={(profileDraft[key] as string) ?? ""}
                        onChange={(e) => setProfileDraft((p) => ({ ...p, [key]: e.target.value || undefined }))}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    ) : (
                      <p className="text-sm text-foreground">{(contact as unknown as Record<string, string>)[key] || <span className="text-muted-foreground/50 italic">—</span>}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
                {editing ? (
                  <textarea
                    rows={4}
                    value={profileDraft.notes ?? ""}
                    onChange={(e) => setProfileDraft((p) => ({ ...p, notes: e.target.value || undefined }))}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  />
                ) : (
                  <p className="text-sm text-foreground whitespace-pre-wrap">{contact.notes || <span className="text-muted-foreground/50 italic">—</span>}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Tags</label>
                {editing ? (
                  <input
                    value={profileDraft.tags?.join(", ") ?? contact.tags?.join(", ") ?? ""}
                    onChange={(e) => setProfileDraft((p) => ({
                      ...p,
                      tags: e.target.value ? e.target.value.split(",").map((t) => t.trim()).filter(Boolean) : [],
                    }))}
                    placeholder="tag1, tag2, tag3"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {contact.tags?.length
                      ? contact.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{tag}</span>
                        ))
                      : <span className="text-sm text-muted-foreground/50 italic">—</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Identifiers tab */}
          {tab === "identifiers" && (
            <div className="lw-contact-identifiers space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setShowAddIdentifier(true)} className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add identifier
                </Button>
              </div>
              {contact.identifiers?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No identifiers yet.</p>
              )}
              <div className="space-y-2">
                {contact.identifiers?.map((id) => (
                  <div key={`${id.identifierType}-${id.value}`} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                        {IDENTIFIER_LABELS[id.identifierType]}
                      </span>
                      <span className="text-sm text-foreground font-medium">{id.value}</span>
                      {id.label && <span className="text-xs text-muted-foreground">{id.label}</span>}
                      {id.isPrimary && <span className="text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5">Primary</span>}
                      {id.isVerified && <span className="text-xs bg-green-500/10 text-green-600 rounded px-1.5 py-0.5">Verified</span>}
                    </div>
                    <button
                      onClick={() => handleRemoveIdentifier(id.identifierType, id.value)}
                      disabled={isSaving}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-50"
                      title="Remove identifier"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attributes tab */}
          {tab === "attributes" && (
            <div className="lw-contact-attributes space-y-6">
              <div className="flex items-center justify-between">
                <Link to="/audience/contact-attributes" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Manage attribute schema →
                </Link>
                {editingAttrs ? (
                  <div className="flex gap-2">
                    <button onClick={() => setEditingAttrs(false)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-accent transition-colors">Cancel</button>
                    <Button onClick={handleSaveAttributes} disabled={isSaving}>{isSaving ? "Saving…" : "Save"}</Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => setEditingAttrs(true)}>Edit</Button>
                )}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}

              {/* Defined attributes section */}
              {definitions.length > 0 && (
                <div className="space-y-2">
                  {definitions.map((def) => {
                    const val = attrDraft[def.key] ?? def.defaultValue ?? "";
                    const inputClass = "w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";
                    return (
                      <div key={def.key} className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3">
                        <div className="w-44 shrink-0">
                          <p className="text-sm font-medium text-foreground">{def.label}</p>
                          <p className="text-xs font-mono text-muted-foreground">{def.key}</p>
                        </div>
                        {editingAttrs ? (
                          (() => {
                            const dt = def.dataType as AttributeDataType;
                            if (dt === "Boolean") {
                              return (
                                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer flex-1">
                                  <input
                                    type="checkbox"
                                    checked={val === "true"}
                                    onChange={(e) => setAttrDraft((p) => ({ ...p, [def.key]: e.target.checked ? "true" : "false" }))}
                                    className="rounded border-border"
                                  />
                                  {val === "true" ? "Yes" : "No"}
                                </label>
                              );
                            }
                            return (
                              <input
                                type={dt === "Number" ? "number" : dt === "Date" ? "date" : "text"}
                                value={val}
                                onChange={(e) => setAttrDraft((p) => ({ ...p, [def.key]: e.target.value }))}
                                className={`${inputClass} flex-1`}
                              />
                            );
                          })()
                        ) : (
                          <span className="text-sm text-foreground flex-1">
                            {val
                              ? (def.dataType === "Boolean" ? (val === "true" ? "Yes" : "No") : val)
                              : <span className="text-muted-foreground/50 italic">—</span>
                            }
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {definitions.length === 0 && Object.keys(attrDraft).length === 0 && !editingAttrs && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No custom attributes.{" "}
                  <Link to="/audience/contact-attributes" className="text-primary hover:underline">Define some</Link>
                </p>
              )}

              {/* Other (freeform) attributes not covered by any definition */}
              {(() => {
                const definedKeys = new Set(definitions.map((d) => d.key));
                const otherEntries = Object.entries(attrDraft).filter(([k]) => !definedKeys.has(k));
                if (otherEntries.length === 0) return null;
                return (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Other attributes</p>
                    <div className="space-y-2">
                      {otherEntries.map(([key, val]) => (
                        <div key={key} className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3">
                          <span className="text-xs font-mono text-muted-foreground w-40 shrink-0">{key}</span>
                          {editingAttrs ? (
                            <div className="flex flex-1 items-center gap-2">
                              <input
                                value={val}
                                onChange={(e) => setAttrDraft((p) => ({ ...p, [key]: e.target.value }))}
                                className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                              />
                              <button
                                onClick={() => setAttrDraft((p) => { const next = { ...p }; delete next[key]; return next; })}
                                className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <span className="text-sm text-foreground">{val}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {editingAttrs && (
                <div className="flex items-end gap-2 rounded-lg border border-dashed border-border p-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Key</label>
                    <input
                      value={newAttrKey}
                      onChange={(e) => setNewAttrKey(e.target.value)}
                      placeholder="attribute_key"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Value</label>
                    <input
                      value={newAttrValue}
                      onChange={(e) => setNewAttrValue(e.target.value)}
                      placeholder="value"
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <Button onClick={handleAddAttr} disabled={!newAttrKey.trim()} className="shrink-0">Add</Button>
                </div>
              )}
            </div>
          )}
          {/* Consent tab */}
          {tab === "consent" && (
            <div className="lw-contact-consent space-y-6">
              {isLoadingConsents ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                    Loading…
                  </div>
                </div>
              ) : (
                <>
                  {/* Channel summary */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(["Email", "Phone"] as ContactChannel[]).map((channel) => {
                      const latest = consents.filter((c) => c.channel === channel)[0] as ContactConsent | undefined;
                      const status: ConsentStatus | null = latest?.status ?? null;
                      return (
                        <div key={channel} className="rounded-lg border border-border bg-card px-4 py-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-foreground">
                              {channel === "Phone" ? "SMS / Phone" : "Email"}
                            </p>
                            {status ? (
                              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                status === "Granted"
                                  ? "bg-green-500/10 text-green-600"
                                  : "bg-red-500/10 text-red-600"
                              }`}>
                                {status}
                              </span>
                            ) : (
                              <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                                Unknown
                              </span>
                            )}
                          </div>
                          {latest ? (
                            <>
                              <p className="text-xs text-muted-foreground">
                                {new Date(latest.timestamp).toLocaleString()}
                              </p>
                              <p className="text-xs text-foreground">{latest.purpose}</p>
                              {latest.consentRevision && (
                                <p className="text-xs text-muted-foreground font-mono">rev: {latest.consentRevision}</p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">No consent recorded for this channel.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* History */}
                  {consents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No consent records for this contact.</p>
                  ) : (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">History</p>
                      <div className="space-y-2">
                        {consents.map((consent) => (
                          <div key={consent.id} className="rounded-lg border border-border bg-card px-4 py-3 space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                consent.status === "Granted"
                                  ? "bg-green-500/10 text-green-600"
                                  : "bg-red-500/10 text-red-600"
                              }`}>
                                {consent.status}
                              </span>
                              <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                                {consent.channel === "Phone" ? "SMS / Phone" : "Email"}
                              </span>
                              <span className="text-sm font-medium text-foreground">{consent.purpose}</span>
                              <span className="ml-auto text-xs text-muted-foreground">
                                {new Date(consent.timestamp).toLocaleString()}
                              </span>
                            </div>
                            {(consent.source || consent.consentRevision || consent.validUntil) && (
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                                {consent.source && (
                                  <p className="text-xs text-muted-foreground">Source: {consent.source}</p>
                                )}
                                {consent.consentRevision && (
                                  <p className="text-xs text-muted-foreground font-mono">rev: {consent.consentRevision}</p>
                                )}
                                {consent.validUntil && (
                                  <p className="text-xs text-muted-foreground">
                                    Expires: {new Date(consent.validUntil).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* History tab */}
          {tab === "history" && (
            <div className="lw-contact-history space-y-4">
              {isLoadingActivityLog ? (
                <p className="text-sm text-muted-foreground text-center py-8">Loading activity…</p>
              ) : activityLog.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No activity recorded for this contact.</p>
              ) : (
                <div className="space-y-2">
                  {activityLog.map((entry) => (
                    <div key={entry.id} className="rounded-lg border border-border bg-card px-4 py-3 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {formatActivityType(entry.activityType)}
                          {entry.fieldName && (
                            <span className="text-muted-foreground font-normal"> — {entry.fieldName}</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {(entry.oldValue || entry.newValue) && (
                        <div className="text-xs text-muted-foreground flex gap-2 items-center">
                          {entry.oldValue && (
                            <span className="line-through">{entry.oldValue}</span>
                          )}
                          {entry.oldValue && entry.newValue && <span>→</span>}
                          {entry.newValue && (
                            <span className="text-foreground">{entry.newValue}</span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        {entry.actorName && <span>By {entry.actorName}</span>}
                        {entry.channel && <span>via {entry.channel}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Contact"
        description={`Are you sure you want to delete "${displayName}"? This action cannot be undone.`}
        confirmLabel={isSaving ? "Deleting…" : "Delete"}
        variant="destructive"
      />

      {showAddIdentifier && contactId && (
        <Modal isOpen={showAddIdentifier} onClose={() => setShowAddIdentifier(false)} title="Add Identifier" size="sm">
          <AddIdentifierModal accountId={accountId} contactId={contactId} onClose={() => setShowAddIdentifier(false)} />
        </Modal>
      )}
    </ProtectedRoute>
  );
}
